/**
 * M5-T4 StocktakeCountPage (issue #616).
 *
 * Tablet-optimised count-entry page. Operator workflow:
 *   1. Page mounts with the stocktake header + all lines.
 *   2. Operator focuses one ingredient at a time, types qty via
 *      the large on-screen numeric keypad, presses "Next" to
 *      commit the line and advance to the next uncounted one.
 *   3. "Save counts" submits every committed line. The `/api` count
 *      endpoint records one ingredient per call, so the hook loops
 *      the batch line-by-line (see useRecordCount).
 *   4. Once every line has a counted_qty the page surfaces a
 *      "Review variance" CTA → /stocktake/:id/review.
 *
 * Tablet ergonomics (mirrors procurement's ReceiveGoodsPage —
 * the project's tablet-precedent reference):
 *   - Keypad keys ≥ 56px (h-14) per the issue body's "> 56px"
 *     spec; secondary controls ≥ 48px (h-12).
 *   - On-screen numeric keypad so operators don't dismiss /
 *     recall the OS soft keyboard for every line.
 *   - Single-line focus pattern; the line list shows progress at
 *     a glance.
 *
 * Numeric discipline:
 *   `system_qty` / `counted_qty` are REAL numbers on the wire.
 *   Keypad raw state is a string (no IEEE-754 jitter accumulation
 *   in the typed value); we convert via Number(...) only at the
 *   submit boundary. Per the backend's `recordCount` validator,
 *   counted_qty must be a finite non-negative number.
 *
 * Permission gate is route-level (`INGREDIENTS_WRITE`).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Delete, RotateCcw, ClipboardCheck, ArrowLeft, X } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  PageShell,
  Button,
  Loading,
} from '@resource-kit/ui/react';
import { useToast } from '@components/ui/Toast';
import { useConfirm } from '@components/ui/ConfirmDialog';
import { formatNumber } from '@lib/utils';
import {
  useStocktakeDetail,
  useRecordCount,
  useCancelStocktake,
  PartialRecordCountError,
} from './hooks/useStocktake';
import type { StocktakeLine } from '@/services';

// Big-key numeric layout used by the on-screen keypad. Mirrors a
// standard calculator (7-8-9 top, 0 bottom) so muscle memory
// carries over from the procurement receive page.
const KEYPAD_KEYS: Array<string> = ['7', '8', '9', '4', '5', '6', '1', '2', '3', '.', '0'];

interface DraftEntry {
  /** Operator-typed raw keypad input. Empty until typed. */
  raw: string;
}

export function StocktakeCountPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { confirm } = useConfirm();
  const { id } = useParams<{ id: string }>();
  const detail = useStocktakeDetail(id);
  const submit = useRecordCount(id ?? '');
  const cancelMut = useCancelStocktake(id ?? '');

  const header = detail.data?.stocktake;
  const lines: StocktakeLine[] = detail.data?.lines ?? [];

  // Sort lines: uncounted first (operator's actionable set), then
  // already-counted (visible for re-entry / verification).
  const sortedLines = useMemo(() => {
    return [...lines].sort((a, b) => {
      const aCounted = a.counted_qty !== null ? 1 : 0;
      const bCounted = b.counted_qty !== null ? 1 : 0;
      if (aCounted !== bCounted) return aCounted - bCounted;
      return a.ingredient_id.localeCompare(b.ingredient_id);
    });
  }, [lines]);

  const [drafts, setDrafts] = useState<Record<string, DraftEntry>>({});
  // Track the active ingredient by **id**, NOT by index — the
  // sortedLines order changes after every successful `recordCount`
  // submit (counted lines slide to the bottom), and an integer
  // `activeIndex` would silently jump to a different ingredient
  // post-refetch. Pinning by id keeps the operator's focus on the
  // same ingredient until they explicitly advance.
  const [activeIngredientId, setActiveIngredientId] = useState<string | null>(null);

  // First-render initialisation: focus the first uncounted line.
  // `useMemo`-stable so the effect's dep array is honest.
  useEffect(() => {
    if (activeIngredientId !== null) return;
    const firstUncounted = sortedLines.find((l) => l.counted_qty === null);
    const fallback = sortedLines[0];
    const target = firstUncounted ?? fallback;
    if (target) setActiveIngredientId(target.ingredient_id);
  }, [sortedLines, activeIngredientId]);

  const activeLineIndex = useMemo(
    () =>
      activeIngredientId === null
        ? -1
        : sortedLines.findIndex((l) => l.ingredient_id === activeIngredientId),
    [activeIngredientId, sortedLines],
  );
  const activeLine = activeLineIndex >= 0 ? sortedLines[activeLineIndex] : undefined;
  const activeDraft = activeLine ? drafts[activeLine.ingredient_id] : undefined;

  const setActiveRaw = useCallback(
    (next: string) => {
      if (!activeLine) return;
      setDrafts((prev) => ({
        ...prev,
        [activeLine.ingredient_id]: { raw: next },
      }));
    },
    [activeLine],
  );

  const press = useCallback(
    (key: string) => {
      if (!activeLine) return;
      const current = activeDraft?.raw ?? '';
      if (key === '.' && current.includes('.')) return;
      // Cap at 3 decimal places (stocktake_lines.system_qty /
      // counted_qty round-trips as REAL; keep entry constrained to
      // the same precision as procurement for parity).
      const dotIdx = current.indexOf('.');
      if (dotIdx !== -1 && current.length - dotIdx > 3) return;
      setActiveRaw(current + key);
    },
    [activeLine, activeDraft, setActiveRaw],
  );

  const backspace = useCallback(() => {
    if (!activeLine) return;
    const current = activeDraft?.raw ?? '';
    setActiveRaw(current.slice(0, -1));
  }, [activeLine, activeDraft, setActiveRaw]);

  const clear = useCallback(() => {
    setActiveRaw('');
  }, [setActiveRaw]);

  /** "Match system" — common operator action when the count agrees
   *  with the system snapshot. Pre-fills the keypad with the
   *  system_qty, the operator presses "Next" to commit. */
  const matchSystem = useCallback(() => {
    if (!activeLine) return;
    const n = activeLine.system_qty;
    const formatted = Number.isInteger(n) ? String(n) : n.toFixed(3).replace(/\.?0+$/, '');
    setActiveRaw(formatted);
  }, [activeLine, setActiveRaw]);

  const next = useCallback(() => {
    if (!activeLine) return;
    // Advance to next ingredient by id (NOT by index — sortedLines
    // re-orders after each successful submit). End-of-list: stay
    // on the current line so "Next" becomes a no-op rather than
    // jumping back to the top.
    const nextIdx = activeLineIndex + 1;
    if (nextIdx >= sortedLines.length) return;
    setActiveIngredientId(sortedLines[nextIdx].ingredient_id);
  }, [activeLine, activeLineIndex, sortedLines]);

  // Hardware-keyboard fallback (mirrors procurement page). Tablets
  // use the on-screen keypad; dev laptops use the keyboard. Enter
  // advances to next line. Keys typed into an open dialog (e.g. the
  // Cmd/Ctrl+K palette) belong to it (#2477).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.target instanceof Element && e.target.closest('[role="dialog"]')) return;
      if (e.key >= '0' && e.key <= '9') press(e.key);
      else if (e.key === '.') press('.');
      else if (e.key === 'Backspace') backspace();
      else if (e.key === 'Enter') next();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [press, backspace, next]);

  if (!id) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-destructive">
          {t('stocktake.count.missingId')}
        </CardContent>
      </Card>
    );
  }
  if (detail.isLoading) return <Loading />;
  if (detail.isError || !header) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-destructive">
          {t('stocktake.count.loadError')}
        </CardContent>
      </Card>
    );
  }

  // If the stocktake is already terminal, redirect to review (the
  // operator should not be entering counts on a closed stocktake).
  const isTerminal = header.status === 'committed' || header.status === 'cancelled';

  // Build the submit payload from drafts. A draft with a non-empty
  // raw string is "ready to save". Validation (positive finite
  // number) happens at submit time; bad rows surface a toast and
  // do not get sent.
  const readyDraftIds = Object.entries(drafts)
    .filter(([, d]) => d.raw !== '')
    .map(([ingredientId]) => ingredientId);

  const handleSave = async () => {
    if (readyDraftIds.length === 0) {
      toast.error(t('stocktake.count.nothingToSave'));
      return;
    }
    const payloadLines: Array<{ ingredient_id: string; counted_qty: number }> = [];
    // Snapshot the raw value alongside each ingredient_id so the
    // post-submit clear can detect whether the operator re-typed
    // mid-flight on the tablet (real race window between
    // `mutateAsync` resolution and the React `setDrafts` callback).
    const submittedSnapshot = new Map<string, string>();
    for (const ingId of readyDraftIds) {
      const raw = drafts[ingId].raw;
      const parsed = Number(raw);
      if (!Number.isFinite(parsed) || parsed < 0) {
        toast.error(t('stocktake.count.invalidQty', { ingredient: ingId }));
        return;
      }
      payloadLines.push({ ingredient_id: ingId, counted_qty: parsed });
      submittedSnapshot.set(ingId, raw);
    }
    try {
      await submit.mutateAsync({ lines: payloadLines });
      toast.success(t('stocktake.count.saveSuccess', { count: payloadLines.length }));
      // Clear drafts ONLY for ingredients whose draft value still
      // matches what we submitted. If the operator re-typed during
      // the async round-trip (tablet operator can be fast), preserve
      // the new draft so the next "Save counts" picks it up rather
      // than silently dropping unsaved work (EQD §3 silent fallback).
      setDrafts((prev) => {
        const next: Record<string, DraftEntry> = { ...prev };
        for (const [id, submittedRaw] of submittedSnapshot) {
          if (next[id] && next[id].raw === submittedRaw) {
            delete next[id];
          }
        }
        return next;
      });
    } catch (err) {
      // Per-line loop can fail mid-batch after some lines persisted.
      // Signal partial progress so the operator knows what survived
      // (the saved lines refresh into view via the hook's onSettled
      // invalidation) rather than assuming the whole batch was lost.
      if (err instanceof PartialRecordCountError && err.savedCount > 0) {
        toast.error(
          t('stocktake.count.partialSaveFailure', {
            saved: err.savedCount,
            count: err.totalCount,
          }),
        );
      } else {
        toast.error(err instanceof Error ? err.message : String(err));
      }
    }
  };

  const handleCancelStocktake = async () => {
    const ok = await confirm({
      title: t('stocktake.count.cancelStocktake'),
      message: t('stocktake.count.cancelConfirm'),
      type: 'danger',
    });
    if (!ok) return;
    try {
      await cancelMut.mutateAsync();
      toast.success(t('stocktake.count.cancelSuccess'));
      navigate('/stocktake');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    }
  };

  const uncountedRemaining = lines.filter((l) => l.counted_qty === null).length;
  const allCounted = lines.length > 0 && uncountedRemaining === 0;

  return (
    <PageShell
      data-testid="stocktake-count-page"
      title={t('stocktake.count.title', { week: header.week_start })}
      subtitle={t('stocktake.count.subtitle', {
        status: t(`stocktake.status.${header.status}`),
        remaining: uncountedRemaining,
        count: lines.length,
      })}
      actions={
        <>
          <Button
            variant="ghost"
            size="sm"
            className="h-12"
            onClick={() => navigate('/stocktake')}
            aria-label={t('stocktake.count.back')}
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          {!isTerminal && (
            <Button
              variant="outline"
              className="h-12"
              onClick={handleCancelStocktake}
              disabled={cancelMut.isPending}
              data-testid="stocktake-cancel"
            >
              <X className="w-4 h-4 mr-2" />
              {t('stocktake.count.cancelStocktake')}
            </Button>
          )}
          {allCounted && (
            <Button
              className="h-12"
              onClick={() => navigate(`/stocktake/${encodeURIComponent(id)}/review`)}
              data-testid="stocktake-review-link"
            >
              <ClipboardCheck className="w-4 h-4 mr-2" />
              {t('stocktake.count.reviewLink')}
            </Button>
          )}
        </>
      }
    >
      <div className="grid gap-4 lg:grid-cols-[1fr_360px]">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('stocktake.count.lines')}</CardTitle>
          </CardHeader>
          <CardContent>
            {sortedLines.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                {t('stocktake.count.emptyLines')}
              </p>
            ) : (
              <ul className="space-y-2">
                {sortedLines.map((line, idx) => {
                  const draft = drafts[line.ingredient_id];
                  const isActive = idx === activeLineIndex;
                  const isCounted = line.counted_qty !== null;
                  return (
                    <li
                      key={line.id}
                      className={`flex items-center justify-between gap-3 p-3 rounded-md border min-h-12 ${
                        isActive
                          ? 'border-primary bg-primary/5'
                          : isCounted
                            ? 'border-success/40 bg-success/10'
                            : 'border-border'
                      }`}
                      data-testid={`stocktake-line-${line.ingredient_id}`}
                    >
                      <button
                        type="button"
                        className="flex-1 text-left h-12 flex flex-col justify-center"
                        onClick={() => setActiveIngredientId(line.ingredient_id)}
                        aria-label={t('stocktake.count.focusLine', {
                          ingredient: line.ingredient_id,
                        })}
                      >
                        <span className="font-medium">{line.ingredient_id}</span>
                        <span className="text-xs text-muted-foreground">
                          {t('stocktake.count.systemQty', {
                            qty: formatNumber(line.system_qty, {
                              maximumFractionDigits: 3,
                            }),
                          })}
                        </span>
                      </button>
                      <div className="text-right tabular-nums min-w-[96px]">
                        <div className="text-lg font-semibold">
                          {draft?.raw ??
                            (line.counted_qty !== null
                              ? formatNumber(line.counted_qty, {
                                  maximumFractionDigits: 3,
                                })
                              : '—')}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {isCounted
                            ? t('stocktake.count.counted')
                            : t('stocktake.count.uncounted')}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {activeLine
                ? t('stocktake.count.keypadFor', {
                    ingredient: activeLine.ingredient_id,
                  })
                : t('stocktake.count.keypadIdle')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="h-16 rounded-md border bg-background flex items-center justify-end px-4 text-3xl tabular-nums font-semibold">
              {activeDraft?.raw || '0'}
            </div>
            <div className="grid grid-cols-3 gap-2">
              {KEYPAD_KEYS.map((key) => (
                <Button
                  key={key}
                  variant="outline"
                  className="h-14 text-xl"
                  onClick={() => press(key)}
                  disabled={!activeLine || isTerminal}
                  data-testid={`stocktake-keypad-${key === '.' ? 'dot' : key}`}
                  aria-label={t('stocktake.count.keypadKey', { key })}
                >
                  {key}
                </Button>
              ))}
              <Button
                variant="outline"
                className="h-14"
                onClick={backspace}
                disabled={!activeLine || isTerminal}
                data-testid="stocktake-keypad-backspace"
                aria-label={t('stocktake.count.backspace')}
              >
                <Delete className="w-5 h-5" />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="ghost"
                className="h-12"
                onClick={clear}
                disabled={!activeLine || isTerminal}
                data-testid="stocktake-keypad-clear"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                {t('stocktake.count.clear')}
              </Button>
              <Button
                variant="ghost"
                className="h-12"
                onClick={matchSystem}
                disabled={!activeLine || isTerminal}
                data-testid="stocktake-keypad-match"
              >
                {t('stocktake.count.matchSystem')}
              </Button>
            </div>
            <Button
              className="w-full h-14 text-base"
              onClick={next}
              disabled={!activeLine || isTerminal}
              data-testid="stocktake-keypad-next"
            >
              {t('stocktake.count.next')}
            </Button>
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-end gap-3">
        <Button
          className="h-12 px-8"
          onClick={handleSave}
          disabled={readyDraftIds.length === 0 || submit.isPending || isTerminal}
          data-testid="stocktake-save"
        >
          {t('stocktake.count.save', { count: readyDraftIds.length })}
        </Button>
      </div>
    </PageShell>
  );
}

export default StocktakeCountPage;
